import React, { ReactNode, useEffect, useState } from 'react';
import { isArray, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import Modal from 'react-modal';
import ReactLoading from 'react-loading';
import { Alert } from 'react-bootstrap';
import { useAsyncCallback } from 'react-async-hook';
import {
  DMSCustomer,
  DMSTaxGroupSummary,
  DMSType,
} from '@upstart/harmony/lib/legos/dmsTypes';
import { isEmbeddedInCommandCenter } from 'util/commandCenter';
import { getCarInfo, sendData } from 'util/amplitude';
import { amplitudeEvents } from 'util/amplitudeEvents';
import './DMSCustomerModal.scss';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import { Deal, TradeIn } from '@upstart/harmony/lib/legos/dealTypes';
import { DealerWithFlags } from '@upstart/harmony/lib/legos/dealerTypes';
import { Car } from '@upstart/harmony/lib/legos/carTypes';
import { Icon, Loader } from 'semantic-ui-react';
import { FinanceResponse } from '@upstart/harmony/lib/legos/financingTypes';
import { FAUX_TRADE_IN_ID } from '@upstart/harmony/lib/legos/constants';
import CloseIcon from '../../Icons/Close';
import DMSService from '../../../services/DMSService';
import DataTable from '../../DataTable';
import dealActions from '../../../redux/deals/actions';
import { useTaxGroupQuery } from '../../../hooks/useTaxGroupQuery';
import {
  canPushTradeInsToDMS,
  validateTradeIns,
} from '../../../util/deskingUtils';
import { TradeInList } from '../../DeskingEditor/TradeInsModal/TradeInsList';
import {
  ErrorMap,
  TradeInField,
} from '../../DeskingEditor/TradeInsModal/TradeInsTableRow';
import FinanceResponseService from '../../../services/FinanceResponseService';

const MODAL_STYLE = {
  content: {
    margin: '10% auto',
    maxWidth: '70%',
    minHeight: '30%',
    maxHeight: '60%',
    position: 'relative' as const,
    padding: 12,
  },
};

const APPLICANT_HEADER = 'Select or Add Applicant in DMS';
const COAPPLICANT_HEADER = 'Select or Add CoApplicant in DMS';
const CUSTOMER_SEARCHING_MESSAGE = 'Searching for existing customer ...';
const CUSTOMER_INSERTING_MESSAGE = 'Adding new customer in DMS ...';
const CUSTOMER_SEARCHING_ERROR = 'Error searching for customer';
const CUSTOMER_INSERTING_ERROR = 'Error adding customer';
const NO_CUSTOMERS_MESSAGE = 'No Customers Found';
const CUSTOMER_TABLE_HEADERS = ['Id', 'Customer', 'Phone', 'Actions'];
const SELECT_CUSTOMER_BUTTON = isEmbeddedInCommandCenter()
  ? 'Continue with customer'
  : 'Continue with this customer';
const ADD_NEW_CUSTOMER_BUTTON = 'Add new customer';

const TAX_GROUP_TABLE_HEADERS = ['Id', 'Description'];
const NO_TAX_GROUPS_MESSAGE = 'No Tax Groups Found';
const SELECT_GROUP_BUTTON = 'Continue with this tax group';
const TAX_GROUPS_ERROR = 'Error getting Tax Groups';
const CUSTOMER_SEARCH_VALIDATION_ERROR_MESSAGE =
  'Please correct the following customer errors to proceed:';
const COAPPLICANT_ENABLED_DMS = [DMSType.CDK, DMSType.TEKION, DMSType.DT];

interface DMSCustomerModalProps {
  deskingOffer?: DeskingOffer;
  deal: Deal;
  dealer: DealerWithFlags;
  onClose: () => void;
  car: Car;
  actions: {
    dealDMSPushOfferRequest: (request: {
      deskingOffer: DeskingOffer;
      deal: Deal;
      dmsCustomers: DMSCustomer[];
      taxGroupId?: string;
    }) => void;
    dealDMSPushRequest: (request: {
      deal: Deal;
      dmsCustomers: DMSCustomer[];
      taxGroupId?: string;
    }) => void;
    updateTradeInsForDMS: (request: {
      deal: Deal;
      financeResponse: FinanceResponse;
    }) => void;
  };
  updateDeskingTradeIns: (tradeIns: TradeIn[]) => Promise<void>;
}

const getTradeInFields = (dmsType?: DMSType | null): TradeInField[] => [
  {
    isEditable: true,
    key: 'id',
    required: dmsType !== DMSType.CDK,
  },
  {
    isEditable: true,
    key: 'year',
    required: true,
  },
  {
    isEditable: true,
    key: 'make',
    required: true,
  },
  {
    isEditable: true,
    key: 'model',
    required: true,
  },
  {
    isEditable: true,
    key: 'trim',
  },
  {
    isEditable: true,
    key: 'mileage',
  },
];

type ScreenType = 'applicant' | 'coapplicant' | 'county';

const DMSCustomerModal = (props: DMSCustomerModalProps) => {
  const {
    deskingOffer,
    deal,
    dealer,
    onClose,
    actions,
    car,
    updateDeskingTradeIns,
  } = props;
  const dmsType = dealer?.dmsType as DMSType;
  const showCountySelection = dmsType === DMSType.DT;
  const validateCoApplicant = !!(
    deskingOffer &&
    dmsType &&
    COAPPLICANT_ENABLED_DMS.includes(dmsType) &&
    deal?.applicant?.hasCoapplicant === 'yes' &&
    !isEmpty(deal?.coapplicant)
  );

  const [screen, setScreen] = useState<ScreenType>('applicant');
  const [selectedCustomers, setSelectedCustomers] = useState<DMSCustomer[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [title, setTitle] = useState(APPLICANT_HEADER);
  const [error, setError] = useState<
    string | undefined | ReactNode | ReactNode[]
  >();
  const [loadingMessage, setLoadingMessage] = useState<string>();
  const [showEditTradeIns, setShowEditTradeIns] = useState(false);
  const [currentTradeIns, setCurrentTradeIns] = useState<
    TradeIn[] | undefined
  >();
  const [updatedTradeIns, setUpdatedTradeIns] = useState<TradeIn[]>([]);
  const [tradeInsLoading, setTradeInsLoading] = useState(false);
  const [tradeInValidationErrors, setTradeInValidationErrors] = useState<
    ErrorMap
  >({});
  const taxGroupQuery = useTaxGroupQuery(
    deal,
    showCountySelection && selectedCustomers.length > 0,
  );
  const asyncResponse = useAsyncCallback<FinanceResponse>(
    FinanceResponseService.get,
  );

  const tradeInFields = getTradeInFields(dealer.dmsType as DMSType);

  useEffect(() => {
    if (deal.selectedFinanceResponseId) {
      asyncResponse.execute(deal.selectedFinanceResponseId);
    }
  }, [deal.selectedFinanceResponseId]);

  useEffect(() => {
    let tradeIns;
    if (
      deskingOffer &&
      deskingOffer?.data?.pencilTradeIns &&
      !canPushTradeInsToDMS(
        deskingOffer.data.pencilTradeIns,
        dealer.dmsType as DMSType,
      )
    ) {
      setShowEditTradeIns(true);
      tradeIns = deskingOffer.data.pencilTradeIns.map(t => ({
        ...t,
        id: t.id === FAUX_TRADE_IN_ID ? '' : t.id,
      }));
      setCurrentTradeIns(tradeIns);
      setUpdatedTradeIns(tradeIns);
      setTradeInValidationErrors(validateTradeIns(tradeIns, tradeInFields));
    } else if (
      !deskingOffer &&
      asyncResponse.result?.data?.tradeIns &&
      !canPushTradeInsToDMS(
        asyncResponse.result.data.tradeIns,
        dealer.dmsType as DMSType,
      )
    ) {
      tradeIns = asyncResponse.result.data.tradeIns.map(t => ({
        ...t,
        id: t.id === FAUX_TRADE_IN_ID ? '' : t.id,
      }));
      setShowEditTradeIns(true);
      setCurrentTradeIns(tradeIns);
      setUpdatedTradeIns(tradeIns);
      setTradeInValidationErrors(validateTradeIns(tradeIns, tradeInFields));
    } else {
      setShowEditTradeIns(false);
    }
  }, [asyncResponse.result, deskingOffer]);

  const asyncCustomerSearch = useAsyncCallback(DMSService.searchCustomer);
  const isLoading =
    asyncCustomerSearch.loading || taxGroupQuery.isLoading || isCreating;

  // Sets view to applicant customer selection screen
  const setApplicantScreen = () => {
    setScreen('applicant');
    setTitle(APPLICANT_HEADER);
    asyncCustomerSearch.reset();
    asyncCustomerSearch.execute(deal, {
      useCoApplicant: false,
      validateCoApplicant,
    });
  };

  // Sets view to coapplicant customer selection screen
  const setCoapplicantScreen = () => {
    setScreen('coapplicant');
    setTitle(COAPPLICANT_HEADER);
    asyncCustomerSearch.reset();
    asyncCustomerSearch.execute(deal, {
      useCoApplicant: true,
      validateCoApplicant,
    });
  };

  // Sets view to county screen
  const setCountyScreen = () => {
    setScreen('county');
    setTitle('Select Tax Group');
    setError('');
  };

  const parseAndSetCustomerValidationErrors = (errObject: any) => {
    const defaultError = <p>CUSTOMER_SEARCHING_ERROR</p>;

    try {
      // Parse errors array which is a specific error response from the customer search endpoint
      const parsedError = JSON.parse(errObject.error);
      if (
        errObject.message === CUSTOMER_SEARCH_VALIDATION_ERROR_MESSAGE &&
        isArray(parsedError?.errors)
      ) {
        const errors = parsedError.errors.map((e: any) => <p>{e}</p>);
        setError([<p>{errObject.message}</p>, <br />, ...errors]);
        return;
      }
    } catch (e) {
      setError(defaultError);
    }
    setError(defaultError);
  };

  const handleCustomerSelection = (dmsCustomer: DMSCustomer) => {
    let dmsCustomers: DMSCustomer[] = [];
    // Update customer ids
    if (screen === 'applicant') {
      dmsCustomers = [dmsCustomer];
    } else if (screen === 'coapplicant') {
      dmsCustomers = [selectedCustomers[0], dmsCustomer];
    }
    setSelectedCustomers(dmsCustomers);

    // determine next screen
    if (screen === 'applicant' && validateCoApplicant) {
      setCoapplicantScreen();
    } else if (
      (screen === 'coapplicant' || screen === 'applicant') &&
      showCountySelection
    ) {
      setCountyScreen();
    } else {
      pushToDMS(dmsCustomers);
    }
  };

  const createCustomer = async () => {
    setIsCreating(true);
    try {
      const createdCustomer = await DMSService.createCustomer(deal, {
        useCoApplicant: screen === 'coapplicant',
      });
      setIsCreating(false);
      handleCustomerSelection(createdCustomer);
    } catch (e) {
      setError((e as { error: string })?.error || CUSTOMER_INSERTING_ERROR);
      sendData(amplitudeEvents.DESKING_PUSHDMS_ERROR, getCarInfo(car));
      setIsCreating(false);
    }
  };

  const handleTaxGroupSelection = (group: DMSTaxGroupSummary) => {
    pushToDMS(selectedCustomers, group);
  };

  const getCustomerRows = (customers: DMSCustomer[]) =>
    customers.map(customer => [
      <span>{customer.id}</span>,
      <span data-dd-privacy="mask" className="amp-mask">
        {customer.fullName}
      </span>,
      <span data-dd-privacy="mask" className="amp-mask">
        {customer.phone}
      </span>,
      <button
        key={customer.id}
        className="select-customer-button modal-button cc-button-primary"
        onClick={() => {
          handleCustomerSelection(customer);
        }}
      >
        {SELECT_CUSTOMER_BUTTON}
      </button>,
    ]);

  const getTaxGroupRows = (groups: DMSTaxGroupSummary[]) =>
    groups.map(group => [
      <span>{group.id}</span>,
      <span>{group.description}</span>,
      <button
        key={group.id}
        className="select-customer-button modal-button"
        onClick={() => {
          handleTaxGroupSelection(group);
        }}
      >
        {SELECT_GROUP_BUTTON}
      </button>,
    ]);

  // remove results of previous queries from state as they will be re-fetched upon modal open
  const closeAndResetState = () => {
    asyncCustomerSearch.reset();
    taxGroupQuery.remove();
    onClose();
  };

  const pushToDMS = async (
    dmsCustomers: DMSCustomer[],
    taxGroup?: DMSTaxGroupSummary,
  ) => {
    if (deskingOffer) {
      actions.dealDMSPushOfferRequest(
        taxGroup
          ? {
              deskingOffer,
              deal,
              dmsCustomers,
              taxGroupId: taxGroup.id,
            }
          : { deskingOffer, deal, dmsCustomers },
      );
    } else {
      actions.dealDMSPushRequest(
        taxGroup
          ? { deal, dmsCustomers, taxGroupId: taxGroup.id }
          : { deal, dmsCustomers },
      );
    }
    sendData(amplitudeEvents.DESKING_PUSHDMS_FINISH, getCarInfo(car));
    onClose();
  };

  // Always begin with primary customer search on load
  useEffect(() => {
    setApplicantScreen();
  }, []);

  useEffect(() => {
    if (asyncCustomerSearch.error) {
      if (
        asyncCustomerSearch.error.message ===
        CUSTOMER_SEARCH_VALIDATION_ERROR_MESSAGE
      ) {
        parseAndSetCustomerValidationErrors(asyncCustomerSearch.error);
        return;
      }
      const theError =
        (asyncCustomerSearch.error &&
          (asyncCustomerSearch.error as any).error) ||
        CUSTOMER_SEARCHING_ERROR;

      setError(theError);
      sendData(amplitudeEvents.DESKING_PUSHDMS_ERROR, getCarInfo(car));
    } else if (taxGroupQuery.isError) {
      const theError =
        (taxGroupQuery.error && (taxGroupQuery.error as any).error) ||
        TAX_GROUPS_ERROR;

      setError(theError);
      sendData(amplitudeEvents.DESKING_PUSHDMS_ERROR, getCarInfo(car));
    }
  }, [asyncCustomerSearch.error, taxGroupQuery.isError, taxGroupQuery.error]);

  useEffect(() => {
    if (asyncCustomerSearch.loading) {
      setLoadingMessage(CUSTOMER_SEARCHING_MESSAGE);
    } else if (isCreating) {
      setLoadingMessage(CUSTOMER_INSERTING_MESSAGE);
    } else if (tradeInsLoading) {
      setLoadingMessage('Loading...');
    }
  }, [asyncCustomerSearch.loading, isCreating, tradeInsLoading]);

  const getCustomerSearchContent = () => (
    <div>
      <div className="dms-customer-modal-header">
        <div>{title}</div>
        <CloseIcon onClick={closeAndResetState} />
      </div>
      {(screen === 'applicant' || screen === 'coapplicant') &&
        asyncCustomerSearch.result && (
          <div className="dms-customer-modal-body">
            <DataTable
              className="dms-customer-modal-table"
              emptyMessage={NO_CUSTOMERS_MESSAGE}
              rows={getCustomerRows(asyncCustomerSearch.result)}
              headers={CUSTOMER_TABLE_HEADERS}
            />
            <div className="action-row">
              <button
                className="add-customer-button modal-button cc-button-outlined"
                onClick={createCustomer}
              >
                {ADD_NEW_CUSTOMER_BUTTON}
              </button>
            </div>
          </div>
        )}
      {screen === 'county' && taxGroupQuery.data && (
        <div className="dms-customer-modal-body">
          <DataTable
            className="dms-customer-modal-table"
            emptyMessage={NO_TAX_GROUPS_MESSAGE}
            rows={getTaxGroupRows(taxGroupQuery.data.groups)}
            headers={TAX_GROUP_TABLE_HEADERS}
          />
        </div>
      )}
      {error && (
        <Alert className="dms-modal-error" bsStyle="danger">
          {error}
        </Alert>
      )}
    </div>
  );

  const onSaveTradeIns = async () => {
    const errors = validateTradeIns(updatedTradeIns ?? [], tradeInFields);

    if (Object.keys(errors).length > 0) {
      setTradeInValidationErrors(errors);
    } else {
      if (deskingOffer) {
        await updateDeskingTradeIns(updatedTradeIns);
      } else if (asyncResponse.result) {
        actions.updateTradeInsForDMS({
          deal: {
            ...deal,
            tradeIns: updatedTradeIns,
          },
          financeResponse: {
            ...asyncResponse.result,
            data: {
              ...asyncResponse.result.data,
              tradeIns: updatedTradeIns,
            },
          },
        });
      }
      setShowEditTradeIns(false);
    }
  };

  const getEditTradeInContent = () => (
    <div>
      <div className="dms-tradein-editor">
        <div className="dms-tradein-editor-header">
          <h4>
            Complete vehicle trade-in details to continue
            {tradeInsLoading && <Loader size="medium" active inline />}
          </h4>
          <CloseIcon onClick={closeAndResetState} />
        </div>
        <Alert>
          <Icon name="exclamation circle" /> Please fill in required fields
          highlighted in red to continue pushing to the DMS
        </Alert>
      </div>
      <div className="dms-tradeins-list">
        <TradeInList
          fields={tradeInFields}
          updatedTradeIns={updatedTradeIns as TradeIn[]}
          dealerId={dealer.id}
          canEditTradeIns
          canDeleteTradeIns={false}
          tradeIns={currentTradeIns as TradeIn[]}
          setUpdatedTradeIns={tradeIns => setUpdatedTradeIns(tradeIns)}
          setIsLoading={val => setTradeInsLoading(val)}
          validationErrors={tradeInValidationErrors}
        />
      </div>
      <div className="action-buttons">
        <button
          className="cc-button-outlined cancelButton capitalize-in-chairman"
          onClick={closeAndResetState}
        >
          Cancel
        </button>
        <button
          className="cc-button-secondary saveButton capitalize-in-chairman"
          onClick={onSaveTradeIns}
        >
          Save and Continue
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen
      style={MODAL_STYLE}
      ariaHideApp={false}
      data-dd-privacy="allow"
    >
      {// eslint-disable-next-line no-nested-ternary
      showEditTradeIns && currentTradeIns ? (
        getEditTradeInContent()
      ) : isLoading ? (
        <div className={'customer-searching-message'}>
          <ReactLoading type="spin" height="20px" width="20px" color="black" />
          <span>{loadingMessage}</span>
        </div>
      ) : (
        getCustomerSearchContent()
      )}
    </Modal>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<{}>) => ({
  actions: bindActionCreators(
    {
      ...dealActions,
    },
    dispatch,
  ),
});

export default connect(null, mapDispatchToProps)(DMSCustomerModal);
